import {
  Alert,
  FormControl,
  Grid2,
  InputLabel,
  Link,
  MenuItem,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { dynamicFormRepository } from "../../../services/dynamicFormRepository";
import { IRiskAssessmentForm } from "../../../services/interfaces/DataInterfaces";
import { AlertType } from "../../../shared/constants/enums";
import { Button } from "../../../shared/ReusableComponents";
import { DynamicForm } from "../../../shared/ReusableComponents/dynamicForms/DynamicForm";
import { hideLoader, showLoader } from "../../../store/currentPreferencesSlice";
import { toast } from "../../../store/toastSlice";
import FormDataHistory from "./FormDataHistory";

export interface IKeyValuePair {
  Key: number;
  Value: string;
}

export const RiskAssessment = ({
  isCurrentUserStudentOrTutor,
  parentId,
}: {
  isCurrentUserStudentOrTutor: boolean;
  parentId: number; //either templateId(SU) or caseId(Student)
}) => {
  const [fieldSchema, setFieldSchema] = useState<any>(undefined);
  const [uiSchema, setUISchema] = useState<any>(undefined);
  const [formData, setFormData] = useState<any>(undefined);
  const [selectedFormType, setSelectedFormType] =
    useState<IRiskAssessmentForm>();
  const [isHistoryVisible, setIsHistoryVisible] = useState<boolean>(false);
  const [historyData, setHistoryData] = useState<any[]>([]);
  const [riskAssessmentForms, setRiskAssessmentForms] = useState<
    IRiskAssessmentForm[]
  >([]);
  const [selectOptions, setSelectOptions] = useState<IKeyValuePair[]>([]);
  const dispatch = useDispatch();
  const auth = useSelector((state: any) => state.login);
  const isCurrentUserCoordinator = auth?.userInfo?.roleId === 2;
  const [offsetError, setOffsetError] = useState<string>("");
  const [offset, setOffset] = useState<number | undefined>(undefined);
  const [errorMessage, setErrorMessage] = useState<string>("");

  //calling API to get forms
  useEffect(() => {
    GetFormsData();
  }, [parentId]);

  useEffect(() => {
    if (selectedFormType?.masterFormId && selectedFormType?.masterFormId > 0) {
      const selectedForm = riskAssessmentForms.find(
        (x) => x.masterFormId === selectedFormType?.masterFormId
      );
      setSelectedFormType(selectedForm);
      setFieldSchema(selectedForm?.fieldSchema);
      setUISchema(selectedForm?.uiSchema);
      setFormData(selectedForm?.dataJson);
      setIsHistoryVisible(false);
      selectedForm?.isFormTypeArray &&
        setHistoryData(selectedForm?.dataJson?.neurovascularItems);
    }
  }, [riskAssessmentForms, selectOptions]);

  useEffect(() => {
    setFieldSchema(selectedFormType?.fieldSchema);
    setUISchema(selectedFormType?.uiSchema);
    setFormData(selectedFormType?.dataJson);
    setOffset(
      selectedFormType?.offsetInMinutes === 0
        ? undefined
        : selectedFormType?.offsetInMinutes
    );
    setErrorMessage("");
    setOffsetError("");
    setIsHistoryVisible(false);
    selectedFormType?.isFormTypeArray &&
      setHistoryData(selectedFormType?.dataJson?.neurovascularItems);
  }, [selectedFormType]);

  const GetFormsData = () => {
    if (isCurrentUserStudentOrTutor) {
      dispatch(showLoader());
      dynamicFormRepository
        .GetDynamicFormsByCaseId(parentId)
        .then((result) => {
          setRiskAssessmentForms(result);
          const forms = result.map((form) => {
            return { Key: form.masterFormId, Value: form.formName };
          });
          const selectedId = selectedFormType?.masterFormId;
          setSelectOptions(forms);
          changeSelectedForm(selectedId);
        })
        .finally(() => {
          dispatch(hideLoader());
        });
    } else {
      dispatch(showLoader());
      dynamicFormRepository
        .GetDynamicFormsByTemplateId(parentId)
        .then((result) => {
          setRiskAssessmentForms(result);
          const forms = result.map((form) => {
            return { Key: form.masterFormId, Value: form.formName };
          });
          const selectedId = selectedFormType?.masterFormId;
          setSelectOptions(forms);
          changeSelectedForm(selectedId);
        })
        .finally(() => {
          dispatch(hideLoader());
        });
    }
  };

  const changeSelectedForm = (selectedFormId: number | undefined) => {
    const selectedForm = riskAssessmentForms.find(
      (x) => x.masterFormId === selectedFormId
    );
    setSelectedFormType(selectedForm);
  };
  const handleSubmit = () => {
    setErrorMessage("");
    if (!isCurrentUserStudentOrTutor) {
      if (offset === 0) {
        setOffsetError("Please enter valid input.");
        return;
      }

      if (!validateDataAndFormat()) {
        return;
      }

      dispatch(showLoader());
      dynamicFormRepository
        .SaveTemplateFormData({
          dataJson: formData,
          templateId: parentId,
          templateFormId: selectedFormType?.templateFormId,
          masterFormId: selectedFormType?.masterFormId ?? 0,
          offsetInMinutes: offset ?? 0,
        })
        .then((result) => {
          dispatch(
            toast({
              isOpen: true,
              message:
                result === "Error"
                  ? result
                  : formData
                  ? "Form updated successfully."
                  : "Form submitted successfully.",
              type: result !== "Error" ? AlertType.SUCCESS : AlertType.ERROR,
            })
          );
        })
        .finally(() => {
          dispatch(hideLoader());
          GetFormsData();
        });
    } else {
      if (!validateDataAndFormat()) {
        return;
      }
      dispatch(showLoader());
      dynamicFormRepository
        .SaveCaseFormData({
          dataJson: formData,
          caseId: parentId,
          caseFormId: selectedFormType?.caseFormId,
          masterFormId: selectedFormType?.masterFormId ?? 0,
        })
        .then((result) => {
          dispatch(
            toast({
              isOpen: true,
              message:
                result === "Error"
                  ? result
                  : formData
                  ? "Form updated successfully."
                  : "Form submitted successfully.",
              type: result !== "Error" ? AlertType.SUCCESS : AlertType.ERROR,
            })
          );
        })
        .finally(() => {
          dispatch(hideLoader());
          GetFormsData();
        });
    }
  };

  const isEmpty = (value: any): boolean => {
    return (
      value === null ||
      value === undefined ||
      value === "" ||
      (Array.isArray(value) && value.length === 0)
    );
  };

  const validateDataAndFormat = () => {
    if (selectedFormType?.description === "neurovascular") {
      formData["neurovascularItems"] = formData?.neurovascularItems?.filter(
        (item: any) => {
          const { date: ignored, ...rest } = item;
          return Object.keys(rest).length > 0;
        }
      );

      const requiredTopLevelFields = [
        "injuryDate",
        "applicationDate",
        "operationDate",
        "affectedLimb",
      ];

      for (const field of requiredTopLevelFields) {
        if (!formData?.[field]) {
          setErrorMessage("All fields are mandatory. Please check.");
          return false;
        }
      }

      if (
        formData?.neurovascularItems?.some((item: any) => {
          return (
            isEmpty(item["date"]) ||
            item["date"] === "Invalid date" ||
            isEmpty(item["colour"]) ||
            isEmpty(item["warmth"]) ||
            isEmpty(item["pulse"]?.["DorsalisPedis"]) ||
            isEmpty(item["pulse"]?.["PosteriorTibialis"]) ||
            isEmpty(item["capillaryRefill"]) ||
            isEmpty(item["movement"]?.["plantarFlexion"]?.["active"]) ||
            isEmpty(item["movement"]?.["plantarFlexion"]?.["passive"]) ||
            isEmpty(item["movement"]?.["dorsiFlexion"]?.["active"]) ||
            isEmpty(item["movement"]?.["dorsiFlexion"]?.["passive"]) ||
            isEmpty(item["sensation"]?.["Nerves"]?.["commonPeroneal"]) ||
            isEmpty(item["sensation"]?.["Nerves"]?.["TibialSuralPlantar"]) ||
            isEmpty(item["painScore"]) ||
            isEmpty(item["swelling"]) ||
            isEmpty(item["bloodLoss"]) ||
            isEmpty(item["comments"]) ||
            isEmpty(item["initials"])
          );
        })
      ) {
        setErrorMessage("All fields are mandatory. Please check.");
        return false;
      }

      if (isCurrentUserStudentOrTutor) {
        formData["readonly"] = "true";
        formData?.neurovascularItems?.forEach((item: any, index: number) => {
          item["readonly"] = "true";
        });
        return true;
      }
    }
    const errors: string[] = [];
    // patient assessment validation for initials field check
    if (selectedFormType?.description === "assessment") {
      const pages = Object.keys(formData?.assessment || {})?.filter(
        (item: any) => {
          const { ...rest } = formData?.assessment[item];
          return (
            Object.keys(rest).length > 0 &&
            isEmpty(formData?.assessment[item].initials)
          );
        }
      );
      if (pages.length > 0) {
        errors.push(`${pages.join(", ")} Initials field is required.`);
      }
    }
    if (errors.length > 0) {
      setErrorMessage(errors.join(" | "));
      return false;
    }

    return true;
  };

  const handleTempFormDataChange = (updatedData: any, error: any): void => {
    if (
      selectedFormType?.description === "neurovascular" &&
      updatedData != null &&
      updatedData["neurovascularItems"] != null
    ) {
      let udata = JSON.parse(JSON.stringify(updatedData));
      udata.neurovascularItems = updatedData.neurovascularItems.map(
        (item: any) => {
          if (Object.keys(item).length === 0) {
            item["date"] = moment().format("DD/MM/YYYY HH:mm");
          }
          return item;
        }
      );
      setFormData(udata);
    } else {
      setFormData(updatedData);
    }
    setErrorMessage("");
  };

  const handleCancel = () => {
    setSelectedFormType(undefined);
  };
  return (
    <div
      className={`${
        isCurrentUserStudentOrTutor ? "student" : "admin"
      }-dynamic-forms dynamic-forms-override`}
    >
      <Grid2>
        <FormControl sx={{ width: "250px", marginBottom: "10px" }}>
          <InputLabel id="form-selection-dropdown-label">
            Select Form Type
          </InputLabel>
          <Select
            variant="outlined"
            required={false}
            labelId="form-selection-dropdown-label"
            id="form-selection-dropdown"
            label="Select Form Type"
            value={selectedFormType?.masterFormId?.toString() ?? ""}
            onChange={(event: SelectChangeEvent) => {
              changeSelectedForm(Number(event.target.value));
            }}
          >
            {selectOptions.map((option, index) => {
              return (
                <MenuItem key={index} value={option.Key}>
                  {option.Value}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
        {selectedFormType?.isFormTypeArray && (
          <Link
            onClick={() => {
              setIsHistoryVisible(!isHistoryVisible);
            }}
            underline="always"
            sx={{
              cursor: "pointer",
              verticalAlign: "middle",
              paddingLeft: "30px",
              lineHeight: "47px",
            }}
          >
            {!isHistoryVisible ? "View History" : "Go Back to Form"}
          </Link>
        )}
      </Grid2>
      {selectedFormType && (
        <React.Fragment>
          {isHistoryVisible ? (
            <FormDataHistory formData={historyData} />
          ) : (
            <React.Fragment>
              {/* {selectedFormType?.isFormTypeArray && !isCurrentUserStudent && (
                <div style={{ margin: "10px" }}>
                  <NewOffsetInput
                    isSingleLineLayout={true}
                    width="310px"
                    onValueChanged={(e) => {
                      setOffsetError("");
                      setOffset(e);
                    }}
                    disabled={isCurrentUserCoordinator}
                    value={offset}
                    errorMessage={offsetError}
                    onlyHighlightOnError={false}
                  />
                </div>
              )} */}
              {selectedFormType?.description === "neurovascular" && (
                <Alert
                  icon={false}
                  style={{
                    fontStyle: "italic",
                    backgroundColor: "#e5f6fd",
                  }}
                >
                  <span>
                    <b>
                      <b>Note</b>: Fields with Asterisk (*) are mandatory to
                      save the form
                    </b>
                  </span>
                </Alert>
              )}
              {fieldSchema && uiSchema && (
                <DynamicForm
                  fieldsSchema={fieldSchema}
                  uiSchema={uiSchema}
                  formData={formData}
                  isReadOnly={isCurrentUserCoordinator}
                  onFormDataChanged={handleTempFormDataChange}
                />
              )}
              {!isCurrentUserCoordinator && (
                <React.Fragment>
                  {errorMessage && (
                    <Alert severity="error" style={{ marginBottom: "10px" }}>
                      {errorMessage}
                    </Alert>
                  )}
                  <div>
                    <Button
                      text="Cancel"
                      variant="outlined"
                      fullWidth={false}
                      onClick={handleCancel}
                    />
                    <Button
                      data-testid="btnSubmit"
                      text={selectedFormType.dataJson ? "Update" : "Submit"}
                      fullWidth={false}
                      className="ml-1"
                      onClick={handleSubmit}
                    />
                  </div>
                </React.Fragment>
              )}
            </React.Fragment>
          )}
        </React.Fragment>
      )}
    </div>
  );
};
